import { useCallback } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import AlertMessage from 'components/common/AlertMessage';
import useTheme from '@mui/system/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';

interface UseEnqueueSnackbarProps {
  onUndo?: () => void;
  delay?: number;
}

interface EnqueueAlertProps {
  delayAlert?: number;
  isPending?: boolean;
  backgroundColor: string;
  color: string;
  onUndo?: () => void;
}
const useEnqueueSnackbar = ({ delay }: Pick<UseEnqueueSnackbarProps, 'delay'> = {}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const enqueueAlert = useCallback(
    (message: string, options: EnqueueAlertProps) => {
      enqueueSnackbar(<AlertMessage message={message} />, {
        autoHideDuration: options?.delayAlert ?? delay ?? 3500,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        style: {
          paddingBottom: 0,
          paddingTop: 0,
          backgroundColor: options.backgroundColor,
          color: options.color,
          borderRadius: theme.shape.borderRadius,
          boxShadow: 'none',
          marginLeft: isMobile ? 0 : 80,
        },
        action: options?.isPending ? (
          <CircularProgress size={24} color={'inherit'} />
        ) : options?.onUndo ? (
          <Button
            sx={(t) => ({ color: t.palette.success.dark, fontSize: t.typography.body2.fontSize })}
            onClick={() => {
              closeSnackbar();
              options?.onUndo?.();
            }}
          >
            Undo
          </Button>
        ) : null,
      });
    },
    [delay]
  );

  const onShowAlert = useCallback(
    (
      message: string,
      { isPending, onUndo, delayAlert }: Pick<EnqueueAlertProps, 'onUndo' | 'isPending' | 'delayAlert'> = {}
    ) => {
      enqueueAlert(message, {
        backgroundColor: 'rgba(237, 247, 237, 1)',
        color: 'rgba(30, 70, 32, 1)',
        onUndo,
        isPending,
        delayAlert,
      });
    },
    [enqueueAlert]
  );

  const onShowInfoAlert = useCallback(
    (
      message: string,
      { isPending, onUndo, delayAlert }: Pick<EnqueueAlertProps, 'onUndo' | 'isPending' | 'delayAlert'> = {}
    ) => {
      enqueueAlert(message, {
        backgroundColor: 'rgba(237, 238, 247, 1)',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        color: theme.palette.text?.primary as string,
        onUndo,
        isPending,
        delayAlert,
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    [enqueueAlert, theme.palette.text.primary]
  );

  const onShowErrorAlert = useCallback(
    (message: string, { delayAlert }: Pick<EnqueueAlertProps, 'delayAlert'> = {}) => {
      enqueueAlert(message, {
        backgroundColor: 'rgba(203, 46, 61, 1)',
        color: 'white',
        delayAlert,
      });
    },
    [enqueueAlert]
  );

  return {
    onShowAlert,
    onShowInfoAlert,
    onShowErrorAlert,
  };
};

export default useEnqueueSnackbar;
